import { pickBy } from 'lodash';
import { CSSProperties } from 'react';
import {
  defaultRenderFileType,
  pngLeftPadding,
  pngScaleFactor,
  renderViewDefaultHorizontalMargin,
  renderViewDefaultTopMargin,
  shareImageHorizontalMargin,
} from '../../../../common/renderer/renderer-settings';
import { RenderFileType, RenderOptions, RenderViewComponentType } from '../../../../common/renderer/types';

export function getScreenshotAreaStyle(options: Partial<RenderOptions>): string {
  if (options.isShareImg) {
    // ShareImage is the dynamic image we generate when people share a net impact
    // profile in the webz, like FB, Twitter, Slack etz.
    const shareImageStyle = `
      margin-top: 100px;
      margin-left: ${shareImageHorizontalMargin};
      margin-right: ${shareImageHorizontalMargin};
    `;
    return shareImageStyle;
  }

  // OA: Style objects are used here to be able to overwrite stuff in order to prevent
  // duplicate keys ending up in final CSS. Yes, it's a bit confusing, and probably unnecessary.
  const baseStyle: CSSProperties = {
    marginTop: options.topMargin !== undefined ? options.topMargin : renderViewDefaultTopMargin,
    position: options.showControlUi ? undefined : 'absolute',
    left: options.showControlUi ? undefined : renderViewDefaultHorizontalMargin,
    marginLeft: options.showControlUi ? renderViewDefaultHorizontalMargin : undefined,
    width: options.width,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  };

  const pngStyle: CSSProperties = getPngStyle(options);
  const pdfStyle: CSSProperties = getPdfStyle(options);
  const componentOverrideStyle: CSSProperties = getComponenOverrideStyle(options);

  const styleString = `
    ${toCSSstring({ ...baseStyle, ...pngStyle, ...pdfStyle, ...componentOverrideStyle })}
    & > * + * {
      margin-top: 60px;
    }
  `;

  return styleString;
}

function getPngStyle(options: Partial<RenderOptions>) {
  const renderFileType = options.renderFileType || defaultRenderFileType;
  if (renderFileType !== RenderFileType.PNG || options.showControlUi) {
    return {};
  }
  const style: CSSProperties = {
    left: pngLeftPadding * pngScaleFactor,
    transform: `scale(${pngScaleFactor})`,
    transformOrigin: 'top left',
  };
  return style;
}

function getPdfStyle(options: Partial<RenderOptions>) {
  const renderFileType = options.renderFileType || defaultRenderFileType;
  if (renderFileType !== RenderFileType.PDF || options.showControlUi) {
    return {};
  }
  // Match the container to the PDF paper width exactly. This used to add
  // `renderViewDefaultHorizontalMargin * 3` of extra width ("to get the
  // legendposition right"), which over-sized the content relative to the
  // paper — Chrome's print shrink-to-fit then scaled everything down ~10%
  // and the `flex-end` alignment turned the slack into a permanent left
  // gutter (PLT-5061). Shrink-to-fit still applies as a safety net if a
  // component genuinely overflows the paper width.
  const style: CSSProperties = {
    width: options.width,
    left: 0,
  };
  return style;
}

const blockDisplayComponentTypes = [
  RenderViewComponentType.VALUES_ALIGNMENT,
  RenderViewComponentType.TOP_HOLDINGS,
  RenderViewComponentType.HOLDINGS_HISTOGRAM,
];

function getComponenOverrideStyle(options: Partial<RenderOptions>): CSSProperties {
  const componentType = options.componentParams?.[0].componentType;
  if (componentType !== undefined && blockDisplayComponentTypes.includes(componentType)) {
    return { display: 'block' };
  }
  return {};
}

function toCSSstring(style: CSSProperties): string {
  const styleWithValues = pickBy(style, value => value !== undefined);
  const styleString = `${Object.entries(styleWithValues)
    .map(
      ([key, value]) =>
        `${key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)}:${
          typeof value === 'number' ? `${value}px` : value
        }`,
    )
    .join(';')};`;
  return styleString;
}
